// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./t3-env.d.ts" />
import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

import { syntheticEmails } from './syntheticEmails';

const isRuntime = process.env['RUNNING_ENV'] === 'runtime';

const runtimeRequiredString = isRuntime ? z.string() : z.string().optional();

const boolean = z
  .string()
  .optional()
  .refine((s) => s === 'true' || s === 'false')
  .transform((s) => s === 'true');

export const env = createEnv({
  skipValidation: process.env['APP_NAME'] === 'bulk-io-service',
  server: {
    // Authentication
    ENABLED_LOGIN_DOMAINS_REGEX: z.string(),
    AUTH_SECRET: runtimeRequiredString,
    WORK_PERMIT_AUTH_KEYCLOAK_ID: z.string().optional(),
    WORK_PERMIT_AUTH_KEYCLOAK_SECRET: z.string().optional(),
    WORK_PERMIT_AUTH_KEYCLOAK_LINK: z.string().url().optional(),
    WORK_PERMIT_AUTH_KEYCLOAK_REALM: z.string().optional(),

    // APIs
    EMAIL_API_KEY: z.string().optional(),
    WEATHER_API_KEY: z.string().optional(),
    NOVU_API_KEY: runtimeRequiredString,
    NOVU_API_URL: runtimeRequiredString,
    API_INTERNAL_BASE_URL: runtimeRequiredString,
    NEXT_API_INTERNAL_URL: runtimeRequiredString,
    WORKFLOW_ENGINE_INTERNAL_URL: runtimeRequiredString,

    // Database
    POSTGRES_PASSWORD: runtimeRequiredString,
    POSTGRES_USER: z.string(),
    POSTGRES_DATABASE: z.string(),
    CLOUD_SQL_CONNECTION_NAME: z.string(),

    // Tenant registry (DB per tenant separation)
    REGISTRY_DATABASE_URL: runtimeRequiredString,

    // Zeebe/Zeeqs
    ZEEBE_GATEWAY_URL: z.string().url(),
    ZEEBE_LOG_LEVEL: z.string().optional(),
    ZEEBE_MAX_RETRIES: z
      .string()
      .transform((s) => Number.parseInt(s, 10))
      .pipe(z.number())
      .optional(),

    ZEEQS_GRAPHQL_URL: z.string().url(),

    /** Set to -1 to disable chunking and send all IDs in a single batch request. */
    ZEEQS_EXPORT_BATCH_SIZE: z
      .string()
      .optional()
      .transform((s) => {
        if (s == null || s === '') {
          return 500;
        }
        const parsed = Number(s);
        if (parsed === -1) {
          return -1;
        }
        if (Number.isFinite(parsed) && parsed >= 1) {
          return Math.floor(parsed);
        }
        return 500;
      }),
    ZEEQS_EXPORT_FALLBACK_ENABLED: z
      .string()
      .optional()
      .transform((s) => s === 'true'),

    // Files upload
    WORK_PERMIT_BUCKET_EMAIL: z.string(),
    WORK_PERMIT_BUCKET_SECRET: z.string().optional(),
    WORK_PERMIT_BUCKET_NAME: z.string(),
    WORK_PERMIT_QUARANTINE_BUCKET_NAME: z.string(),
    WORK_PERMIT_FILE_VIEWER: z.string(),
    WORK_PERMIT_PROJECT_ID: z.string(),

    // Reports upload (async CSV exports — insafe_reports_* buckets)
    WORK_PERMIT_REPORTS_BUCKET_EMAIL: z.string().optional(),
    WORK_PERMIT_REPORTS_BUCKET_SECRET: z.string().optional(),

    // Sentry
    NEXT_ENABLE_SENTRY_LOGS: z.string().optional(),
    NEXT_SENTRY_DSN: z.string().url(),
    NEXT_SENTRY_TRACES_SAMPLE_RATE: z
      .string()
      .transform((s) => Number.parseFloat(s))
      .pipe(z.number())
      .optional(),

    // Memwatch
    NEXT_ENABLE_MEMWATCH: z.string().optional(),

    // NodeJs
    NEXT_RUNTIME: z.string(),
    APP_ENV: z.string().optional(),

    // API logging
    LOG_LEVEL_WORKFLOWINSTANCE: z.string().optional(),

    DISABLE_NOVU_NOTIFICATIONS_TRIGGER: boolean,
    SYNTHETIC_EMAILS: syntheticEmails,

    // AI URL
    AI_API_BASE_URL: z.string().optional(),
    AI_API_TOKEN: z.string().optional(),

    // Data ingestion service URL (separate from the AI/copilot service)
    DATA_INGESTION_API_BASE_URL: z.string().optional(),
    DATA_INGESTION_API_TOKEN: z.string().optional(),

    // Bulk IO service (async CSV exports, bulk site upload processing)
    BULK_IO_SERVICE_URL: z.string().url().optional(),
    BULK_IO_SERVICE_API_KEY: z.string().optional(),

    // Notification Center SSE (overrides Flipt when set to true/false)
    NOTIFICATION_SSE_ENABLED: z.enum(['true', 'false']).optional(),

    // Sequin CDC audit (WAL annotations + webhook). Set false to disable the full pipeline.
    SEQUIN_WEBHOOK_SECRET: z.string().optional(),
    SEQUIN_AUDIT_ENABLED: z
      .enum(['true', 'false'])
      .optional()
      .default('true')
      .transform((value) => value === 'true'),

    // Flipt Feature Flags
    FLIPT_API_URL: z.string().url().optional(),
    FLIPT_API_TOKEN: z.string().optional(),
    FLIPT_NAMESPACE: z.string().optional().default('default'),
    FLIPT_ENVIRONMENT: z.string().optional(),
    FLIPT_REFRESH_INTERVAL: z.string().optional().default('30'),
    FEATURE_FLAG_PROVIDER: z.enum(['flipt']).optional().default('flipt'),
  },
  client: {
    // Client-side only variables

    // App
    NEXT_PUBLIC_LOG_LEVEL: z.string().optional(),
    NEXT_PUBLIC_ENABLE_PERMIT_ACCESS_LOGS: z.string().optional(),

    NEXT_PUBLIC_WORK_PERMIT_AUTH_KEYCLOAK_ENABLED: z.string().optional(),

    NEXT_PUBLIC_MAP_API_KEY: z.string(),
    NEXT_PUBLIC_MAP_MAP_ID: z.string(),
    NEXT_PUBLIC_NOVU_APP_ID: z.string(),
    NEXT_PUBLIC_NOVU_WS: z.string().url(),
    NEXT_PUBLIC_APP_TITLE: z.string().optional(),
    NEXT_PUBLIC_ENABLE_E2E: z.string().optional(),
    NEXT_PUBLIC_LOGO_IMAGE: z.string().optional(),
    NEXT_PUBLIC_ZEEBE_PLAY_BASE: z.string().optional(),
    NEXT_PUBLIC_APP_VERSION: z.string(),
    NEXT_PUBLIC_GRAPHQL_URL: z.string().url().optional(),
    NEXT_PUBLIC_WEBSOCKET_URL: z.string().url().optional(),
    NEXT_PUBLIC_WORK_PERMIT_AUTH_KEYCLOAK_TENANTS: z.string().optional(),
    NEXT_PUBLIC_WORK_PERMIT_WEB_VITALS_ENABLED: boolean,
    NEXT_PUBLIC_WORK_PERMIT_WEB_VITALS_LOG_LEVEL: z.string().optional(),

    NEXT_PUBLIC_PERMITS_TABLE_SHOW_DEBUG_COLUMNS: boolean,

    NEXT_PUBLIC_PERMIT_SHOW_ALERTS: boolean,

    NEXT_PUBLIC_FORM_DEBUG_ENABLED: boolean,

    // For BPMN variables, we need to define variables with the leading NEXT_PUBLIC_BPMN
    NEXT_PUBLIC_BPMN_AUTO_POPULATE_TIME: z.string().optional(),

    // files upload
    NEXT_PUBLIC_UPLOAD_FILE_SIZE: z.string(),

    NEXT_PUBLIC_DISABLE_WEBSOCKET_ALERTS: z.string().optional(),

    // App Environment (client-side)
    NEXT_PUBLIC_APP_ENV: z.string().optional(),

    // Sentry (client-side)
    NEXT_PUBLIC_SENTRY_DSN: z.string().url().optional(),
    NEXT_PUBLIC_SENTRY_LOGS: z.string().optional(),
    NEXT_PUBLIC_ENABLE_SENTRY_SESSION_REPLAY: z.string().optional(),
    NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE: z
      .string()
      .transform((s) => Number.parseFloat(s))
      .pipe(z.number())
      .optional(),
    NEXT_PUBLIC_SENTRY_REPLAY_SAMPLE_RATE: z
      .string()
      .transform((s) => Number.parseFloat(s))
      .pipe(z.number())
      .optional(),

    // AI URL
    NEXT_PUBLIC_AI_API_BASE_URL: z.string().optional(),
    NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES: z.string().optional(),
    NEXT_PUBLIC_IDLE_WARNING_SECONDS: z.string().optional(),
    NEXT_PUBLIC_IDLE_TIMEOUT_ENABLED: z.string().optional(),

    // Notification Center SSE (overrides Flipt when set to true/false)
    NEXT_PUBLIC_NOTIFICATION_SSE_ENABLED: z.enum(['true', 'false']).optional(),
  },
  runtimeEnv: {
    // App
    NEXT_PUBLIC_LOG_LEVEL: process.env['NEXT_PUBLIC_LOG_LEVEL'],
    NEXT_PUBLIC_ENABLE_PERMIT_ACCESS_LOGS:
      process.env['NEXT_PUBLIC_ENABLE_PERMIT_ACCESS_LOGS'],

    // Authentication
    ENABLED_LOGIN_DOMAINS_REGEX: process.env['ENABLED_LOGIN_DOMAINS_REGEX'],
    AUTH_SECRET: process.env['AUTH_SECRET'],
    WORK_PERMIT_AUTH_KEYCLOAK_ID: process.env['WORK_PERMIT_AUTH_KEYCLOAK_ID'],
    WORK_PERMIT_AUTH_KEYCLOAK_SECRET:
      process.env['WORK_PERMIT_AUTH_KEYCLOAK_SECRET'],
    WORK_PERMIT_AUTH_KEYCLOAK_LINK:
      process.env['WORK_PERMIT_AUTH_KEYCLOAK_LINK'],
    WORK_PERMIT_AUTH_KEYCLOAK_REALM:
      process.env['WORK_PERMIT_AUTH_KEYCLOAK_REALM'],
    NEXT_PUBLIC_WORK_PERMIT_AUTH_KEYCLOAK_ENABLED:
      process.env['NEXT_PUBLIC_WORK_PERMIT_AUTH_KEYCLOAK_ENABLED'],
    NEXT_PUBLIC_WORK_PERMIT_AUTH_KEYCLOAK_TENANTS:
      process.env['NEXT_PUBLIC_WORK_PERMIT_AUTH_KEYCLOAK_TENANTS'],
    // APIs
    EMAIL_API_KEY: process.env['EMAIL_API_KEY'],
    WEATHER_API_KEY: process.env['WEATHER_API_KEY'],
    NOVU_API_KEY: process.env['NOVU_API_KEY'],
    NOVU_API_URL: process.env['NOVU_API_URL'],
    API_INTERNAL_BASE_URL: process.env['API_INTERNAL_BASE_URL'],
    NEXT_API_INTERNAL_URL: process.env['NEXT_API_INTERNAL_URL'],
    WORKFLOW_ENGINE_INTERNAL_URL: process.env['WORKFLOW_ENGINE_INTERNAL_URL'],

    // Database
    POSTGRES_PASSWORD: process.env['POSTGRES_PASSWORD'],
    POSTGRES_USER: process.env['POSTGRES_USER'],
    POSTGRES_DATABASE: process.env['POSTGRES_DATABASE'],
    CLOUD_SQL_CONNECTION_NAME: process.env['CLOUD_SQL_CONNECTION_NAME'],
    REGISTRY_DATABASE_URL: process.env['REGISTRY_DATABASE_URL'],

    // Zeebe/Zeeqs
    ZEEBE_GATEWAY_URL: process.env['ZEEBE_GATEWAY_URL'],
    ZEEBE_LOG_LEVEL: process.env['ZEEBE_LOG_LEVEL'],
    ZEEBE_MAX_RETRIES: process.env['ZEEBE_MAX_RETRIES'],
    ZEEQS_GRAPHQL_URL: process.env['ZEEQS_GRAPHQL_URL'],
    ZEEQS_EXPORT_BATCH_SIZE: process.env['ZEEQS_EXPORT_BATCH_SIZE'],
    ZEEQS_EXPORT_FALLBACK_ENABLED: process.env['ZEEQS_EXPORT_FALLBACK_ENABLED'],

    // Files upload
    WORK_PERMIT_BUCKET_EMAIL: process.env['WORK_PERMIT_BUCKET_EMAIL'],
    WORK_PERMIT_BUCKET_SECRET: process.env['WORK_PERMIT_BUCKET_SECRET'],
    WORK_PERMIT_BUCKET_NAME: process.env['WORK_PERMIT_BUCKET_NAME'],
    WORK_PERMIT_QUARANTINE_BUCKET_NAME:
      process.env['WORK_PERMIT_QUARANTINE_BUCKET_NAME'],
    WORK_PERMIT_FILE_VIEWER: process.env['WORK_PERMIT_FILE_VIEWER'],
    WORK_PERMIT_PROJECT_ID: process.env['WORK_PERMIT_PROJECT_ID'],
    WORK_PERMIT_REPORTS_BUCKET_EMAIL:
      process.env['WORK_PERMIT_REPORTS_BUCKET_EMAIL'],
    WORK_PERMIT_REPORTS_BUCKET_SECRET:
      process.env['WORK_PERMIT_REPORTS_BUCKET_SECRET'],

    // Sentry
    NEXT_PUBLIC_ENABLE_SENTRY_SESSION_REPLAY:
      process.env['NEXT_PUBLIC_ENABLE_SENTRY_SESSION_REPLAY'],
    NEXT_ENABLE_SENTRY_LOGS: process.env['NEXT_ENABLE_SENTRY_LOGS'],
    NEXT_SENTRY_DSN: process.env['NEXT_SENTRY_DSN'],

    // Memwatch
    NEXT_ENABLE_MEMWATCH: process.env['NEXT_ENABLE_MEMWATCH'],

    // NodeJs
    NEXT_RUNTIME: process.env['NEXT_RUNTIME'],
    APP_ENV: process.env['APP_ENV'],

    // Client-side only variables
    NEXT_PUBLIC_ENABLE_E2E: process.env['NEXT_PUBLIC_ENABLE_E2E'],
    NEXT_PUBLIC_APP_TITLE: process.env['NEXT_PUBLIC_APP_TITLE'],
    NEXT_PUBLIC_LOGO_IMAGE: process.env['NEXT_PUBLIC_LOGO_IMAGE'],
    NEXT_PUBLIC_MAP_API_KEY: process.env['NEXT_PUBLIC_MAP_API_KEY'],
    NEXT_PUBLIC_MAP_MAP_ID: process.env['NEXT_PUBLIC_MAP_MAP_ID'],
    NEXT_PUBLIC_NOVU_APP_ID: process.env['NEXT_PUBLIC_NOVU_APP_ID'],
    NEXT_PUBLIC_NOVU_WS: process.env['NEXT_PUBLIC_NOVU_WS'],

    NEXT_PUBLIC_ZEEBE_PLAY_BASE: process.env['NEXT_PUBLIC_ZEEBE_PLAY_BASEPATH'],

    NEXT_PUBLIC_GRAPHQL_URL: process.env['NEXT_PUBLIC_GRAPHQL_URL'],
    NEXT_PUBLIC_WEBSOCKET_URL: process.env['NEXT_PUBLIC_WEBSOCKET_URL'],
    NEXT_PUBLIC_APP_VERSION: process.env['NEXT_PUBLIC_APP_VERSION'],

    NEXT_PUBLIC_WORK_PERMIT_WEB_VITALS_ENABLED:
      process.env['NEXT_PUBLIC_WORK_PERMIT_WEB_VITALS_ENABLED'],
    NEXT_PUBLIC_WORK_PERMIT_WEB_VITALS_LOG_LEVEL:
      process.env['NEXT_PUBLIC_WORK_PERMIT_WEB_VITALS_LOG_LEVEL'],

    NEXT_PUBLIC_PERMITS_TABLE_SHOW_DEBUG_COLUMNS:
      process.env['NEXT_PUBLIC_PERMITS_TABLE_SHOW_DEBUG_COLUMNS'],

    NEXT_PUBLIC_PERMIT_SHOW_ALERTS:
      process.env['NEXT_PUBLIC_PERMIT_SHOW_ALERTS'],

    NEXT_PUBLIC_BPMN_AUTO_POPULATE_TIME:
      process.env['NEXT_PUBLIC_BPMN_AUTO_POPULATE_TIME'],

    NEXT_PUBLIC_FORM_DEBUG_ENABLED:
      process.env['NEXT_PUBLIC_FORM_DEBUG_ENABLED'],

    // API logging
    LOG_LEVEL_WORKFLOWINSTANCE: process.env['LOG_LEVEL_WORKFLOWINSTANCE'],

    DISABLE_NOVU_NOTIFICATIONS_TRIGGER:
      process.env['DISABLE_NOVU_NOTIFICATIONS_TRIGGER'],
    SYNTHETIC_EMAILS: process.env['SYNTHETIC_EMAILS'],

    // files upload
    NEXT_PUBLIC_UPLOAD_FILE_SIZE: process.env['NEXT_PUBLIC_UPLOAD_FILE_SIZE'],

    NEXT_PUBLIC_DISABLE_WEBSOCKET_ALERTS:
      process.env['NEXT_PUBLIC_DISABLE_WEBSOCKET_ALERTS'],

    // App Environment (client-side)
    NEXT_PUBLIC_APP_ENV: process.env['NEXT_PUBLIC_APP_ENV'],

    // Sentry (client-side)
    NEXT_PUBLIC_SENTRY_DSN: process.env['NEXT_PUBLIC_SENTRY_DSN'],
    NEXT_PUBLIC_SENTRY_LOGS: process.env['NEXT_PUBLIC_SENTRY_LOGS'],
    NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE:
      process.env['NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE'],
    NEXT_PUBLIC_SENTRY_REPLAY_SAMPLE_RATE:
      process.env['NEXT_PUBLIC_SENTRY_REPLAY_SAMPLE_RATE'],

    AI_API_BASE_URL: process.env['AI_API_BASE_URL'],
    AI_API_TOKEN: process.env['AI_API_TOKEN'],
    NEXT_PUBLIC_AI_API_BASE_URL: process.env['NEXT_PUBLIC_AI_API_BASE_URL'],
    DATA_INGESTION_API_BASE_URL: process.env['DATA_INGESTION_API_BASE_URL'],
    DATA_INGESTION_API_TOKEN: process.env['DATA_INGESTION_API_TOKEN'],
    BULK_IO_SERVICE_URL: process.env['BULK_IO_SERVICE_URL'],
    BULK_IO_SERVICE_API_KEY: process.env['BULK_IO_SERVICE_API_KEY'],
    NOTIFICATION_SSE_ENABLED: process.env['NOTIFICATION_SSE_ENABLED'],
    NEXT_SENTRY_TRACES_SAMPLE_RATE:
      process.env['NEXT_SENTRY_TRACES_SAMPLE_RATE'],
    SEQUIN_WEBHOOK_SECRET: process.env['SEQUIN_WEBHOOK_SECRET'],
    SEQUIN_AUDIT_ENABLED: process.env['SEQUIN_AUDIT_ENABLED'],

    // Flipt Feature Flags
    FLIPT_API_URL: process.env['FLIPT_API_URL'],
    FLIPT_API_TOKEN: process.env['FLIPT_API_TOKEN'],
    FLIPT_NAMESPACE: process.env['FLIPT_NAMESPACE'],
    FLIPT_ENVIRONMENT: process.env['FLIPT_ENVIRONMENT'],
    FEATURE_FLAG_PROVIDER: process.env['FEATURE_FLAG_PROVIDER'],
    FLIPT_REFRESH_INTERVAL: process.env['FLIPT_REFRESH_INTERVAL'],
    NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES:
      process.env['NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES'],
    NEXT_PUBLIC_IDLE_WARNING_SECONDS:
      process.env['NEXT_PUBLIC_IDLE_WARNING_SECONDS'],
    NEXT_PUBLIC_IDLE_TIMEOUT_ENABLED:
      process.env['NEXT_PUBLIC_IDLE_TIMEOUT_ENABLED'],
    NEXT_PUBLIC_NOTIFICATION_SSE_ENABLED:
      process.env['NEXT_PUBLIC_NOTIFICATION_SSE_ENABLED'],
  },
});
